import { useEffect, useMemo, useState } from 'react'

import { useCommercialStructure } from 'commons/customHooks'
import {
  ActionTabs,
  Badge,
  Flex,
  Icon,
  type ItemActionTabsType,
  LoadingHavaianas,
  Page,
  SlimButton,
  Text,
} from 'components'
import { t } from 'i18next'
import type { SalesCalendarListItem } from 'services/salesCalendar/interfaces'
import { getSalesCalendarList, setPage } from 'store/SalesCalendarContext/salesCalendarSlice'
import { getCommercialStructure } from 'store/SharedContext/sharedSlice'
import { useDispatch, useSelector } from 'store/store'
import { useAppTheme } from 'styles/theme'

import {
  CalendarCreationModal,
  CalendarCreationModalAction,
} from './components/CalendarCreationModal'
import { CalendarDeleteModal } from './components/CalendarDeleteModal'
import { CalendarsTable } from './components/CalendarsTable/CalendarsTable'
import { CalendarTrackingModal } from './components/CalendarTrackingModal'
import * as S from './styles'

enum TabsIndex {
  DEFAULT = '0',
}

export const SalesCalendar = () => {
  const { colors, sizes } = useAppTheme()
  const dispatch = useDispatch()
  const {
    loadingCalendars,
    loadingSaveCalendar,
    calendars,
    pagination: { currentPage, pageSize },
  } = useSelector((state) => state.salesCalendar)
  const { getBMUs, getOfficesByBMU, hasCommercialStructure } = useCommercialStructure()
  const { loadingCommercialStructure } = useSelector((state) => state.shared)

  const [selectedBMUIndex, setSelectedBMUIndex] = useState<string>(TabsIndex.DEFAULT)
  const [selectedOfficeIndex, setSelectedOfficeIndex] = useState<string>(TabsIndex.DEFAULT)
  const [openCalendarCreationModal, setOpenCalendarCreationModal] = useState<boolean>(false)
  const [openCalendarDeleteModal, setOpenCalendarDeleteModal] = useState<boolean>(false)
  const [openTrackingModal, setOpenTrackingModal] = useState<boolean>(false)
  const [modalAction, setModalAction] = useState<CalendarCreationModalAction>(
    CalendarCreationModalAction.CREATE,
  )
  const [selectedCalendarData, setSelectedCalendarData] = useState<
    SalesCalendarListItem | undefined
  >(undefined)

  const bmus = getBMUs()
  const selectedBMU = bmus[+selectedBMUIndex]?.bmuCode ?? ''
  const officesFromBMU = getOfficesByBMU(selectedBMU)
  const selectedOffice = officesFromBMU[+selectedOfficeIndex]?.id ?? ''

  const bmuTabs: ItemActionTabsType[] = bmus.map((bmu, index) => ({
    label: bmu.bmuName,
    onClick: () => {
      setSelectedBMUIndex(index.toString())
      setSelectedOfficeIndex(TabsIndex.DEFAULT)
      dispatch(setPage(1))
    },
  }))

  const officeTabs: ItemActionTabsType[] = officesFromBMU.map((office, index) => ({
    label: office.id,
    onClick: () => {
      setSelectedOfficeIndex(index.toString())
      dispatch(setPage(1))
    },
  }))

  const handleOpenCalendarCreationModal = (
    action: CalendarCreationModalAction,
    calendarData?: SalesCalendarListItem,
  ) => {
    setModalAction(action)
    setSelectedCalendarData(calendarData)
    setOpenCalendarCreationModal(true)
  }

  const handleOpenCalendarDeleteModal = (calendarData: SalesCalendarListItem) => {
    setSelectedCalendarData(calendarData)
    setOpenCalendarDeleteModal(true)
  }

  const handleCalendarCreationSuccess = () => {
    dispatch(getSalesCalendarList(selectedOffice, 1, pageSize))
  }

  const checkedCalendars = useMemo(() => {
    const checkedCalendars = calendars.filter((calendar) => calendar.checked)
    return checkedCalendars
  }, [calendars])

  useEffect(() => {
    if (!hasCommercialStructure) dispatch(getCommercialStructure())
  }, [hasCommercialStructure])

  useEffect(() => {
    if (hasCommercialStructure && selectedOffice) {
      dispatch(getSalesCalendarList(selectedOffice, currentPage, pageSize))
    }
  }, [selectedOffice, currentPage, pageSize, hasCommercialStructure, dispatch])

  return (
    <Page
      fullwidth
      loading={loadingCommercialStructure}
      renderHeaderComponent={
        <S.TitleBar>
          <Text color="strong" size="sm" weight="md">
            {t('screens.salesCalendar.title')}
          </Text>
        </S.TitleBar>
      }
    >
      <Flex direction="column" gap="md">
        <Flex alignItems="center" fullWidth justifyContent="space-between">
          <ActionTabs
            currentItem={selectedBMUIndex}
            disabled={loadingCalendars}
            items={bmuTabs}
            variant="contained"
          />

          {!loadingCalendars && (
            <Flex alignItems="center" gap="sm">
              <Badge appearance="soft" color={checkedCalendars.length > 0 ? 'info' : 'neutral'}>
                {t('screens.salesCalendar.periodSelected', { count: checkedCalendars.length })}
              </Badge>
              <SlimButton
                content={t('buttons.tracking')}
                disabled={checkedCalendars.length === 0}
                icon={<Icon icon="track" size={sizes.ion.icon.small} />}
                onClick={() => setOpenTrackingModal(true)}
                size="lg"
                variant="white"
              />
            </Flex>
          )}
        </Flex>

        <S.Container>
          <Flex alignItems="center" fullWidth justifyContent="space-between">
            <Flex alignItems="center" gap="md">
              <S.IconContainer>
                <Icon
                  color={colors.ion.primary.onsofter}
                  icon="calendar-plus"
                  size={sizes.ion.global.x32}
                />
              </S.IconContainer>
              <Flex direction="column" gap="xs">
                <Text color="stronger" size="md" weight="md">
                  {t('screens.salesCalendar.addNewPeriod')}
                </Text>
                <Text color="strong" size="xs">
                  {t('screens.salesCalendar.createAndManageCalendar')}
                </Text>
              </Flex>
            </Flex>
            <SlimButton
              content={t('screens.salesCalendar.newPeriod')}
              icon={<Icon icon="plus" size={sizes.ion.icon.medium} />}
              loading={loadingSaveCalendar}
              onClick={() => handleOpenCalendarCreationModal(CalendarCreationModalAction.CREATE)}
              variant="primary"
            />
          </Flex>
        </S.Container>

        <S.Container>
          <S.TabsWrapper>
            <ActionTabs
              currentItem={selectedOfficeIndex}
              disabled={loadingCalendars}
              items={officeTabs}
            />
          </S.TabsWrapper>
          {loadingCalendars ? (
            <LoadingHavaianas />
          ) : (
            <CalendarsTable
              onDelete={(item) => handleOpenCalendarDeleteModal(item)}
              onEdit={(item) =>
                handleOpenCalendarCreationModal(CalendarCreationModalAction.EDIT, item)
              }
            />
          )}
        </S.Container>
      </Flex>

      {openCalendarCreationModal && (
        <CalendarCreationModal
          action={modalAction}
          calendarData={selectedCalendarData}
          defaultOffice={selectedOffice}
          onCreationSuccess={handleCalendarCreationSuccess}
          open={openCalendarCreationModal}
          setOpen={setOpenCalendarCreationModal}
        />
      )}

      {openCalendarDeleteModal && (
        <CalendarDeleteModal
          calendarData={selectedCalendarData}
          open={openCalendarDeleteModal}
          setOpen={setOpenCalendarDeleteModal}
        />
      )}

      {openTrackingModal && (
        <CalendarTrackingModal
          checkedCalendars={checkedCalendars}
          defaultOfficeIndex={selectedOfficeIndex}
          offices={officesFromBMU}
          open={openTrackingModal}
          setOpen={setOpenTrackingModal}
        />
      )}
    </Page>
  )
}
